import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { downloadFile, errMsg, filterHazards, nzDateTime } from "@/lib/hsUtil";
import HazardFilters from "./HazardFilters";
import TemplatesBar from "./TemplatesBar";

const Marker = ({ h }) => (
  <>
    {h.archived && <span className="text-amber-400 text-xs ml-2" data-testid={`jh-archived-${h.id}`}>Archived</span>}
    {h.updated_since_added && <span className="text-sky-300 text-xs ml-2" data-testid={`jh-updated-${h.id}`}>Updated {nzDateTime(h.content_updated_at).slice(0, 10)}</span>}
  </>
);

const PickRow = ({ h, on, onToggle }) => (
  <label className={`flex gap-2 p-2 rounded border text-sm cursor-pointer ${on ? "border-silver" : "border-neutral-800"}`} data-testid={`jh-pick-row-${h.id}`}>
    <input type="checkbox" checked={on} disabled={h.archived && !on} onChange={() => onToggle(h.id, !on)} data-testid={`jh-pick-${h.id}`}/>
    <span><span className="text-xs opacity-60">{h.ref_id} · {h.build_phase}</span><br/>{h.hazard}<Marker h={h}/></span>
  </label>
);

const SelectedRow = ({ h, on, onTick }) => (
  <div className="border border-neutral-800 rounded p-2 text-sm flex gap-2" data-testid={`jh-selected-${h.id}`}>
    <input type="checkbox" checked={on} onChange={() => onTick(h.id)} className="mt-1" data-testid={`jh-selected-tick-${h.id}`}/>
    <div>
    <div><span className="text-xs opacity-60">{h.ref_id}</span> {h.hazard}<Marker h={h}/></div>
    {h.controls && <div className="text-xs opacity-80 mt-1"><span className="text-silver">Controls: </span>{h.controls}</div>}
    <div className="text-xs mt-1"><span className="text-silver">Residual risk: </span>{h.residual_risk || "-"}</div>
    </div>
  </div>
);

export default function JobHazardsTab() {
  const [sites, setSites] = useState([]);
  const [pid, setPid] = useState("");
  const [master, setMaster] = useState([]);
  const [job, setJob] = useState(null);
  const [f, setF] = useState({ q: "", phase: "", risk: "", flag: "" });
  const [ticked, setTicked] = useState(() => new Set());
  useEffect(() => {
    api.get("/hs/sites").then(r => setSites(r.data));
    api.get("/hs/hazard-library").then(r => setMaster(r.data));
  }, []);
  const loadJob = useCallback(() => { if (pid) api.get(`/hs/job-hazards/${pid}`).then(r => setJob(r.data)); }, [pid]);
  useEffect(() => { setJob(null); setTicked(new Set()); loadJob(); }, [loadJob]);
  const selected = useMemo(() => new Set(job?.selected_ids || []), [job]);
  const pool = useMemo(() => master.filter(h => !h.archived || selected.has(h.id)), [master, selected]);
  const shown = useMemo(() => filterHazards(pool, f), [pool, f]);

  async function change(add, remove) {
    try { const r = await api.post(`/hs/job-hazards/${pid}`, { add, remove }); setJob(r.data); return true; }
    catch (e) { toast.error(errMsg(e)); return false; }
  }
  const toggle = (id, on) => change(on ? [id] : [], on ? [] : [id]);
  const selectPhase = () => change(shown.filter(h => !h.archived && !selected.has(h.id)).map(h => h.id), []);
  const clearShown = () => {
    const ids = shown.filter(h => selected.has(h.id)).map(h => h.id);
    if (ids.length && window.confirm(`Untick ${ids.length} hazard(s) for this project?`)) change([], ids);
  };
  const name = sites.find(s => s.id === pid)?.name || "project";
  const selTicked = (job?.selected || []).filter(h => ticked.has(h.id)).map(h => h.id);
  const allSelOn = !!job?.selected.length && selTicked.length === job.selected.length;
  const tickSel = id => setTicked(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const tickAllSel = () => setTicked(allSelOn ? new Set() : new Set(job.selected.map(h => h.id)));
  async function clearTicked() {
    if (!selTicked.length || !window.confirm(`Remove ${selTicked.length} hazard(s) from ${name}? Nothing is deleted from the master register.`)) return;
    if (await change([], selTicked)) { setTicked(new Set()); toast.success(`${selTicked.length} hazard(s) removed from this project`); }
  }
  const exp = ext => downloadFile(`/hs/job-hazards/${pid}/register.${ext}`, `site_hazard_register_${name}.${ext}`).catch(e => toast.error(errMsg(e)));

  return (
    <div className="space-y-3" data-testid="hs-job-hazards-tab">
      <div className="card flex flex-wrap items-end gap-3">
        <label className="text-sm"><span className="block text-xs text-silver">Project</span>
          <select className="input" value={pid} onChange={e => setPid(e.target.value)} data-testid="jh-job-select">
            <option value="">Choose a project…</option>
            {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select></label>
        {job && <span className="text-sm" data-testid="jh-selected-count">{job.selected.length} hazards selected</span>}
        {job && <button className="btn-ghost text-sm" onClick={() => exp("pdf")} data-testid="jh-export-pdf">Site Hazard Register PDF</button>}
        {job && <button className="btn-ghost text-sm" onClick={() => exp("csv")} data-testid="jh-export-csv">Export CSV</button>}
      </div>
      {job && <TemplatesBar pid={pid} selectedCount={job.selected.length} onApplied={d => setJob(d)}/>}
      {job && (
        <div className="grid lg:grid-cols-2 gap-3">
          <div className="card space-y-2">
            <h3 className="text-silver">Master register: tick what applies to this project</h3>
            <HazardFilters rows={pool} f={f} setF={setF} prefix="jh" showRisk={false}/>
            <div className="flex gap-2 flex-wrap">
              <button className="btn-ghost text-xs" disabled={!f.phase} onClick={selectPhase} data-testid="jh-select-phase"
                title={f.phase ? "" : "Pick a build phase first"}>Select all in this phase</button>
              <button className="btn-ghost text-xs" onClick={clearShown} data-testid="jh-clear">Clear</button>
              <span className="text-xs opacity-60 self-center">{shown.length} shown</span>
            </div>
            <div className="space-y-1 max-h-[60vh] overflow-y-auto">
              {shown.map(h => <PickRow key={h.id} h={h} on={selected.has(h.id)} onToggle={toggle}/>)}
            </div>
          </div>
          <div className="card space-y-2">
            <h3 className="text-silver">Selected for {job.project.name}</h3>
            {job.selected.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="jh-selected-bulk-bar">
                <label className="flex items-center gap-1"><input type="checkbox" checked={allSelOn} onChange={tickAllSel} data-testid="jh-selected-tick-all"/> Select all</label>
                <span data-testid="jh-selected-tick-msg">{selTicked.length ? `${selTicked.length} of ${job.selected.length} selected` : ""}</span>
                <button className="btn-ghost text-xs" disabled={!selTicked.length} onClick={clearTicked} data-testid="jh-clear-selected">Clear selected hazards</button>
              </div>
            )}
            {job.selected.length === 0 && <div className="opacity-60 text-sm">No hazards selected yet.</div>}
            <div className="space-y-1 max-h-[70vh] overflow-y-auto">{job.selected.map(h => <SelectedRow key={h.id} h={h} on={ticked.has(h.id)} onTick={tickSel}/>)}</div>
          </div>
        </div>
      )}
    </div>
  );
}
